import { useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles, Trash2, X } from "lucide-react";
import { API, api } from "../lib/api";
import { useBusiness } from "../context/BusinessContext";

const SUGGESTIONS = ["Bu ayki kasa açıklarımı yorumla", "Giderlerimi nasıl azaltabilirim?", "Gün sonu kasa sayımı nasıl yapılır?"];

async function streamChat(body, onToken) {
  const res = await fetch(`${API}/ai/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("hn_token")}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Asistan şu an kullanılamıyor.");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const d = p.replace(/^data: /, "");
      if (d === "[DONE]") return;
      const j = JSON.parse(d);
      if (j.error) throw new Error(j.error);
      onToken(j.t);
    }
  }
}

function Bubble({ m }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
          mine ? "rounded-br-md bg-gradient-to-r from-cyan-600 to-blue-800 text-white" : "rounded-bl-md bg-slate-100 text-slate-800"
        }`}
      >
        {m.content || <span className="animate-pulse">Yazıyor…</span>}
      </div>
    </div>
  );
}

export function AIAssistant() {
  const { current } = useBusiness();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/ai/history").then((r) => setMsgs(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = async (t) => {
    const message = (t ?? text).trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    const append = (tok) =>
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + tok }]);
    try {
      await streamChat({ message, business_id: current?.id }, append);
    } catch (e) {
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: e.message }]);
    }
    setBusy(false);
  };

  const clear = async () => {
    await api.delete("/ai/history");
    setMsgs([]);
  };

  return (
    <>
      <button
        data-testid="ai-assistant-toggle-button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-900 text-white shadow-xl shadow-cyan-900/30 transition-transform hover:scale-105 active:scale-95"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>
      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed bottom-24 right-3 z-50 flex h-[70vh] max-h-[600px] w-[calc(100vw-1.5rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl animate-in slide-in-from-bottom-4 fade-in duration-200 sm:right-5"
        >
          <div className="flex items-center justify-between bg-gradient-to-r from-cyan-600 to-blue-900 px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              <div>
                <div className="font-heading text-sm font-bold">Kasam Asistan</div>
                <div className="text-[11px] text-cyan-100">Yapay zeka finans yardımcınız</div>
              </div>
            </div>
            <button data-testid="ai-clear-history-button" onClick={clear} title="Sohbeti temizle" className="rounded-full p-1.5 hover:bg-white/15">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="ai-messages">
            {msgs.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-slate-500">Merhaba! Kasanız, girdi-çıktılarınız ve muhasebe hakkında sorabilirsiniz.</p>
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={s}
                    data-testid={`ai-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="block w-full rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-left text-sm text-cyan-900 transition-colors hover:bg-cyan-100"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <Bubble key={i} m={m} />
            ))}
            <div ref={endRef} />
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex gap-2 border-t border-slate-200 p-3"
          >
            <input
              data-testid="ai-message-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Bir soru yazın…"
              className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30"
            />
            <button
              data-testid="ai-send-button"
              disabled={busy}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-cyan-600 to-blue-800 text-white disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
